'use client';

import Image from 'next/image';
import {useSessionStorage} from 'usehooks-ts';
import {twJoin, twMerge} from 'tailwind-merge';
import {IconEdit, IconHeartShare, IconPlayerPlayFilled} from '@tabler/icons-react';
import FadingImage from 'src/components/FadingImage';
import BaseButton from 'src/components/BaseButton';
import getImageUrl from 'src/utils/getImageUrl';
import recordEvent from 'src/utils/recordEvent';
import pluralize from 'src/utils/pluralize';
import Avatar from 'src/components/Avatar';
import ShareSheet from '../ShareSheet';
import Dropdown from '../Dropdown';
import Footer from '../Footer';

const imageClassName = 'nice-image rounded-xl [box-shadow:var(--quiz-shadow)] dark:[box-shadow:none] after:rounded-xl';

type Props = {
  quiz: QuizWithAuthorAndPlaces;
  session: IronSession | null;
};

const QuizPageContent = ({quiz, session}: Props) => {
  const [, , resetAnswers] = useSessionStorage<Record<string, string>>(`given_answers_${quiz._id}`, {});

  return (
    <div className="relative flex flex-col w-full max-w-[1320px] grow mx-auto px-(--page-padding) items-center">
      <div className="flex items-center gap-6 w-full grow max-[900px]:flex-col">
        <div className="relative grow w-0 max-[900px]:w-full max-[900px]:max-w-[400px] max-[450px]:max-w-[300px] flex justify-center py-20 max-[900px]:py-10 my-8 max-[900px]:my-4 max-[450px]:my-0">
          <div className="w-[55%] aspect-9/10 relative rounded-xl">
            {quiz.questions[1]?.image ? (
              <div
                className={twJoin(
                  imageClassName,
                  'w-full h-full absolute -bottom-[16%] -left-[10%] opacity-0 animate-[slide-in_0.8s_0.6s_cubic-bezier(0.2,0.92,0.34,1)_forwards]'
                )}
              >
                <FadingImage
                  priority
                  src={getImageUrl(quiz.questions[1]?.image?.path, 'medium')}
                  alt={quiz.name}
                  fill
                  className="object-cover"
                />
              </div>
            ) : null}

            {quiz.questions[2]?.image ? (
              <div
                className={twJoin(
                  imageClassName,
                  'w-full h-full absolute -top-[16%] -right-[10%] opacity-0 animate-[slide-in_0.8s_0.3s_cubic-bezier(0.2,0.92,0.34,1)_forwards]'
                )}
              >
                <FadingImage
                  priority
                  src={getImageUrl(quiz.questions[2]?.image?.path, 'medium')}
                  alt={quiz.name}
                  fill
                  className="object-cover"
                />
              </div>
            ) : null}

            {quiz.questions[0]?.image ? (
              <div
                className={twMerge(
                  imageClassName,
                  'flex justify-center items-center h-full w-full opacity-0 animate-[slide-in_0.8s_0.1s_cubic-bezier(0.2,0.92,0.34,1)_forwards]'
                )}
              >
                <Image
                  priority
                  src={getImageUrl(quiz.questions[0]?.image?.path, 'medium')}
                  alt={quiz.name}
                  className="object-cover"
                  fill
                />

                {/* This is a full-res image preloaded for the transition animation */}
                <Image
                  src={getImageUrl(quiz.questions[0]?.image?.path)}
                  alt={quiz.name}
                  aria-hidden
                  className="object-cover"
                  fill
                />
              </div>
            ) : null}
          </div>
        </div>

        <div className="opacity-0 animate-[slide-in_0.8s_0.1s_cubic-bezier(0.2,0.92,0.34,1)_forwards] grow w-0 max-[900px]:w-full max-[900px]:mb-12 flex flex-col gap-6 max-sm:gap-4 text-(--quiz-text-color)">
          <h1 className="text-6xl max-xl:text-5xl max-lg:text-4xl max-sm:text-2xl font-semibold font-heading">
            {quiz.name}
          </h1>

          <div className="flex items-center gap-3 text-xl max-lg:text-lg max-sm:text-base">
            {quiz.author ? (
              <>
                <Avatar user={quiz.author} size={24} />
                <span>{quiz.author.name}</span>
                <span>•</span>
              </>
            ) : null}

            <span>
              {quiz.questions.length} {pluralize('question', quiz.questions.length)}
            </span>
          </div>

          <p className="text-xl max-lg:text-lg">{quiz.description}</p>

          <div className="w-full flex flex-wrap max-[400px]:flex-col max-[400px]:gap-2 gap-4 mt-8 max-lg:mt-0">
            <BaseButton
              TablerIcon={IconPlayerPlayFilled}
              iconSize={22}
              label="Start Quiz"
              buttonVariant="primary"
              className="pl-5"
              size="large"
              onPress={resetAnswers}
              href={`/quiz/${quiz.nameId}/play`}
            />

            <Dropdown items={[{component: <ShareSheet key={0} />}]}>
              <BaseButton
                TablerIcon={IconHeartShare}
                iconSize={22}
                label="Share"
                buttonVariant="translucent"
                size="large"
                className="[&_.icon-wrapper]:-mt-[3px]"
                onPress={() => {
                  if (!session?.isAdmin) recordEvent('shareQuiz', {props: {quiz: quiz.nameId}});
                }}
              />
            </Dropdown>

            {quiz.author?._id === session?._id || session?.isAdmin ? (
              <BaseButton
                TablerIcon={IconEdit}
                iconSize={22}
                label="Edit"
                buttonVariant="text"
                href={`/quiz/${quiz.nameId}/edit`}
                size="large"
              />
            ) : null}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default QuizPageContent;
